/**
 * Bulk Operations Component
 * Mass enrollment, updates, and reporting for HOA/Association properties
 * Handle multiple units/properties simultaneously with progress tracking
 */

import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Upload, Download, Users, Settings, FileText, CheckCircle, AlertCircle, Clock } from 'lucide-react';

interface BulkOperation {
  id: string;
  type: 'enrollment' | 'updates' | 'maintenance' | 'reports';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  total: number;
  completed: number;
  failed: number;
  created_at: string;
  completed_at?: string;
  error_message?: string;
}

interface BulkEnrollmentData {
  property_name: string;
  building_name: string;
  unit_number: string;
  owner_name: string;
  owner_email: string;
  monthly_fee: number;
  bedrooms: number;
  bathrooms: number;
  square_footage: number;
}

export default function BulkOperations() {
  const [activeTab, setActiveTab] = useState<'enrollment' | 'updates' | 'maintenance' | 'reports'>('enrollment');
  const [operations, setOperations] = useState<BulkOperation[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed': return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'processing': return <Clock className="w-5 h-5 text-blue-600 animate-spin" />;
      default: return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setUploadProgress(0);

    // Simulate file processing
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsProcessing(false);
          setShowUploadModal(false);
          // Add new operation to the list
          const newOperation: BulkOperation = {
            id: Date.now().toString(),
            type: activeTab,
            status: 'completed',
            progress: 100,
            total: 150, // Simulated
            completed: 148,
            failed: 2,
            created_at: new Date().toISOString(),
            completed_at: new Date().toISOString()
          };
          setOperations(prev => [newOperation, ...prev]);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const downloadTemplate = () => {
    let headers = '';
    let sampleData = '';

    switch (activeTab) {
      case 'enrollment':
        headers = 'Property Name,Building Name,Unit Number,Owner Name,Owner Email,Monthly Fee,Bedrooms,Bathrooms,Square Footage';
        sampleData = 'Sunset Condos,Building A,101,John Doe,john@example.com,2500,2,2,1200';
        break;
      case 'updates':
        headers = 'Unit ID,Property Name,Owner Name,Owner Email,Monthly Fee,Status';
        sampleData = 'unit_123,Sunset Condos,Jane Smith,jane@example.com,2600,occupied';
        break;
      case 'maintenance':
        headers = 'Property Name,Building Name,Unit Number,Maintenance Type,Frequency,Priority';
        sampleData = 'Sunset Condos,Building A,101,HVAC Service,Quarterly,medium';
        break;
    }

    const csvContent = `${headers}\n${sampleData}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Bulk Operations</h2>
          <p className="text-gray-600">Manage multiple properties and units simultaneously</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={downloadTemplate}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download Template
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Upload CSV
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1">
        {[
          { id: 'enrollment', label: 'Bulk Enrollment', icon: Users },
          { id: 'updates', label: 'Bulk Updates', icon: Settings },
          { id: 'maintenance', label: 'Maintenance Schedule', icon: FileText },
          { id: 'reports', label: 'Bulk Reports', icon: FileText }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
              activeTab === tab.id
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Recent Operations */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Operations</h3>
        {operations.length === 0 ? (
          <div className="py-8 text-center text-gray-400">
            No bulk operations yet. Upload a CSV file to get started.
          </div>
        ) : (
          <div className="space-y-4">
            {operations.map((operation) => (
              <div key={operation.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(operation.status)}
                    <div>
                      <h4 className="font-semibold text-gray-900 capitalize">
                        {operation.type} Operation
                      </h4>
                      <p className="text-sm text-gray-600">
                        {new Date(operation.created_at).toLocaleDateString()} at{' '}
                        {new Date(operation.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(operation.status)}`}>
                    {operation.status}
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                  <div>
                    <span className="font-medium">Progress:</span>
                    <p>{operation.completed}/{operation.total} ({Math.round((operation.completed / operation.total) * 100)}%)</p>
                  </div>
                  <div>
                    <span className="font-medium">Completed:</span>
                    <p className="text-green-600">{operation.completed}</p>
                  </div>
                  <div>
                    <span className="font-medium">Failed:</span>
                    <p className="text-red-600">{operation.failed}</p>
                  </div>
                  <div>
                    <span className="font-medium">Duration:</span>
                    <p>{operation.completed_at ?
                      `${Math.round((new Date(operation.completed_at).getTime() - new Date(operation.created_at).getTime()) / 1000)}s` :
                      'In progress'
                    }</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      operation.status === 'completed' ? 'bg-green-600' :
                      operation.status === 'failed' ? 'bg-red-600' :
                      'bg-blue-600'
                    }`}
                    style={{ width: `${(operation.completed / operation.total) * 100}%` }}
                  ></div>
                </div>

                {operation.error_message && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                    <p className="text-sm text-red-800">{operation.error_message}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Operation Configuration */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {activeTab === 'enrollment' && 'Bulk Unit Enrollment'}
          {activeTab === 'updates' && 'Bulk Property Updates'}
          {activeTab === 'maintenance' && 'Maintenance Scheduling'}
          {activeTab === 'reports' && 'Bulk Report Generation'}
        </h3>

        <div className="space-y-4">
          {activeTab === 'enrollment' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Enrollment Settings</h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Send welcome emails to new owners</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Auto-generate maintenance schedules</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm">Create default service agreements</span>
                  </label>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Data Validation</h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Validate email addresses</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Check for duplicate units</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm">Verify property exists</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'updates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Update Options</h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Update owner information</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Update monthly fees</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm">Update unit status</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm">Update lease information</span>
                  </label>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Notification Settings</h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Notify owners of changes</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm">Log all changes for audit</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm">Create backup before updates</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'maintenance' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Schedule Settings</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default Frequency</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option>Monthly</option>
                      <option>Quarterly</option>
                      <option>Semi-Annual</option>
                      <option>Annual</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Auto-assign technicians</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm">Send reminders to owners</span>
                  </label>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Maintenance Types</h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">HVAC Service</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Plumbing Inspection</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Electrical Check</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm">Appliance Testing</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Report Types</h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Occupancy Report</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Maintenance Summary</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm">Financial Summary</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm">Service History</span>
                  </label>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Report Settings</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option>Last 30 days</option>
                      <option>Last 90 days</option>
                      <option>Last 6 months</option>
                      <option>Last year</option>
                      <option>Custom range</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option>PDF</option>
                      <option>Excel</option>
                      <option>CSV</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Upload CSV File</h3>
              <p className="text-sm text-gray-600 mt-1">
                Upload your {activeTab} data file
              </p>
            </div>

            <div className="p-6">
              {isProcessing ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Processing file...</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">{uploadProgress}% complete</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">
                    Drag and drop your CSV file here, or click to browse
                  </p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer"
                  >
                    Select File
                  </label>
                </div>
              )}
            </div>

            {!isProcessing && (
              <div className="px-6 py-4 border-t flex justify-end gap-3">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
